import isNumber from 'lodash/isNumber';
import { computed } from 'vue';

import { COMMUNITY_PERMISSION_TYPES } from '@/app/community/constants';
import { BRAND, USER } from '@/models/auth/permissions.enum';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';

export function useCommunityPermissions() {
  // stores
  const identityStore = useIdentityStore();
  const flagStore = useFlagStore();

  // methods
  function getBrandById(brandId) {
    return identityStore.identityBrandsById[brandId] ?? null;
  }

  function verifyBrandAndUserAccess(brand, permissions = COMMUNITY_PERMISSION_TYPES.COMMUNITY) {
    let selectedBrand = brand;
    if (isNumber(brand)) {
      selectedBrand = getBrandById(brand);
    }

    const { BRAND_PERMISSION, USER_PERMISSION } = permissions;
    let brandCanAccess = false;
    if (BRAND_PERMISSION) {
      brandCanAccess = identityStore.guard(BRAND_PERMISSION, selectedBrand);
    }
    if (USER_PERMISSION) {
      const userCanAccess = identityStore.guard(USER_PERMISSION, selectedBrand);
      return brandCanAccess && userCanAccess;
    }
    return brandCanAccess;
  }

  function brandHasAccessToSalesforceIntegration(brandId) {
    const brand = isNumber(brandId) ? getBrandById(brandId) : brandId;
    if (!brand) return false;

    const hasBrandPermission = identityStore.guard(BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS, brand);
    const hasFeatureFlag = flagStore.ready && flagStore.flags.communitySalesforceIntegration;

    return hasBrandPermission && hasFeatureFlag;
  }

  // computed
  const userCanAccessCommunity = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_COMMUNITY),
  );

  const brandCanAccessCommunity = computed(() =>
    identityStore.guard(BRAND.COMMUNITY.CAN_ACCESS_COMMUNITY),
  );

  const canAccessCommunity = computed(
    () => brandCanAccessCommunity.value && userCanAccessCommunity.value,
  );

  const canAccessSentimentAnalysis = computed(
    () =>
      identityStore.guard(USER.COMMUNITY.CAN_ACCESS_TEXT_ANALYSIS) &&
      identityStore.guard(BRAND.COMMUNITY.CAN_ACCESS_TEXT_ANALYSIS),
  );

  const brandCanAccessInboxAutomation = computed(() =>
    identityStore.guard(BRAND.COMMUNITY.CAN_ACCESS_INBOX_AUTOMATION),
  );

  const brandCanAccessInternalNotes = computed(() =>
    identityStore.guard(BRAND.COMMUNITY.CAN_ACCESS_COMMUNITY_COLLABORATION),
  );

  const userCanAccessCommunityOverview = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_COMMUNITY_OVERVIEW),
  );

  const brandCanAccessCommunityOverview = computed(() =>
    identityStore.guard(BRAND.COMMUNITY.CAN_ACCESS_COMMUNITY_OVERVIEW),
  );

  const canAccessCommunityOverview = computed(
    () => userCanAccessCommunityOverview.value && brandCanAccessCommunityOverview.value,
  );

  const canAccessInstagramDirect = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_INSTAGRAM_DIRECT),
  );

  const canAccessInstagramComments = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_INSTAGRAM_COMMENTS),
  );

  const canAccessTikTokComments = computed(
    () =>
      identityStore.guard(BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS) &&
      identityStore.guard(USER.COMMUNITY.CAN_ACCESS_TIKTOK_COMMENTS),
  );

  const canAccessFacebookMessenger = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_FACEBOOK_MESSENGER),
  );

  const canAccessFacebookComments = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_FACEBOOK_COMMENTS),
  );

  const canAccessTwitterMentions = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_TWITTER_MENTIONS),
  );

  const canAccessTwitterDirect = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_TWITTER_DIRECT),
  );

  const canAccessYouTubeComments = computed(() =>
    identityStore.guard(USER.COMMUNITY.CAN_ACCESS_YOUTUBE_COMMENTS),
  );

  const canAccessCommunityTranslation = computed(() => {
    if (!flagStore.ready) return undefined;
    return flagStore.ready && flagStore?.flags?.communityTranslation;
  });

  const canAccessYoutubeCompliance = computed(
    () => flagStore.ready && flagStore.flags.youtubeCompliance,
  );

  const hasAccessToIntegrations = computed(() =>
    identityStore.guard(BRAND.SETTINGS.CAN_ACCESS_INTEGRATIONS),
  );

  const hasMultiBrandInboxFlag = computed(
    () => flagStore.ready && flagStore.flags.communityMultiBrandInbox,
  );

  const canAccessMultiBrandInbox = computed(
    () =>
      identityStore.allActiveUserBrands.some((brand) => {
        const brandAccess = identityStore.guard(
          BRAND.COMMUNITY.CAN_ACCESS_MULTI_BRAND_INBOX,
          brand,
        );
        const userAccess = identityStore.guard(USER.COMMUNITY.CAN_ACCESS_MULTI_BRAND_INBOX, brand);
        return brandAccess && userAccess;
      }) && hasMultiBrandInboxFlag.value,
  );

  const canAccessSalesforceIntegration = computed(
    () => flagStore.ready && flagStore.flags.communitySalesforceIntegration,
  );

  const canAccessAutomationRuleGDI = computed(
    () => flagStore.ready && flagStore.flags.automationRuleGdi,
  );

  const canAccessAiCategory = computed(
    () => flagStore.ready && flagStore.flags.communityAiCategory,
  );

  const canAccessAutomationBusinessHours = computed(
    () => flagStore.ready && flagStore.flags.communityAutomationBusinessHours,
  );

  const canAccessInitiateDM = computed(
    () => flagStore.ready && flagStore.flags.communityDmFromComment,
  );

  return {
    getBrandById,
    verifyBrandAndUserAccess,
    brandHasAccessToSalesforceIntegration,
    userCanAccessCommunity,
    brandCanAccessCommunity,
    canAccessCommunity,
    canAccessSentimentAnalysis,
    brandCanAccessInternalNotes,
    brandCanAccessCommunityOverview,
    brandCanAccessInboxAutomation,
    canAccessCommunityOverview,
    userCanAccessCommunityOverview,
    canAccessInstagramDirect,
    canAccessInstagramComments,
    canAccessTikTokComments,
    canAccessFacebookMessenger,
    canAccessFacebookComments,
    canAccessTwitterMentions,
    canAccessTwitterDirect,
    canAccessYouTubeComments,
    canAccessCommunityTranslation,
    canAccessYoutubeCompliance,
    hasAccessToIntegrations,
    canAccessMultiBrandInbox,
    hasMultiBrandInboxFlag,
    canAccessSalesforceIntegration,
    canAccessAutomationRuleGDI,
    canAccessAiCategory,
    canAccessAutomationBusinessHours,
    canAccessInitiateDM,
  };
}
